"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <>
      {}
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map(item => <NavigationMenuItem key={item.id} data-index={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="hover:!bg-transparent focus:!bg-transparent group inline-flex h-9 w-max items-center justify-center px-4 py-2 text-sm font-semibold text-foreground transition-all duration-[var(--duration-fast)] hover:text-foreground relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary-light after:transition-all after:duration-[var(--duration-medium)] hover:after:w-full focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
            <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link to="/team-and-contact#contact-us" className="hover:!bg-transparent focus:!bg-transparent group inline-flex h-9 w-max items-center justify-center px-4 py-2 text-sm font-semibold text-foreground transition-all duration-[var(--duration-fast)] hover:text-foreground relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary-light after:transition-all after:duration-[var(--duration-medium)] hover:after:w-full focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring">
                Contact us
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
      {}
      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {}
      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-soft md:hidden">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="px-4 py-3 text-sm font-semibold text-foreground hover:text-foreground hover:bg-accent rounded-md transition-all duration-[var(--duration-fast)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
            <Link to="/team-and-contact#contact-us" className="px-4 py-3 text-sm font-semibold text-foreground hover:text-foreground hover:bg-accent rounded-md transition-all duration-[var(--duration-fast)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring" onClick={() => setMobileMenuOpen(false)}>
              Contact us
            </Link>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "py-3 bg-background/80 backdrop-blur-sm shadow-soft border-b border-border/50" : "py-4 bg-background border-b border-border"}`}>
      <div className="container mx-auto px-4 flex items-center justify-between gap-6">
        {}
        <Link to="/" className="shrink-0 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring rounded-sm">
          <img src="https://grown-elk-1.10web.cloud/wp-content/uploads/2026/02/Sloane_Logo-170x49-1.png" alt="Sloane" className="!h-[42px]" />
        </Link>

        {}
        <MenuProvider menu_id="3" className="flex-1 flex justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        {}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <Button asChild variant="outline" className="rounded-[var(--radius-full)] px-6 py-3 hover:!bg-transparent focus:!bg-transparent hover:border-primary hover:text-primary transition-all duration-[var(--duration-fast)]">
            <Link to="mailto:mail@sloane.co.uk">Email</Link>
          </Button>
          <Button asChild className="rounded-[var(--radius-full)] px-6 py-3 bg-primary-dark text-foreground-primary hover:bg-primary-dark hover:shadow-medium hover:-translate-y-0.5 focus:!bg-primary-dark transition-all duration-[var(--duration-fast)]">
            <Link to="tel:+442079460958" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-9 has-[>svg]:px-3 rounded-[var(--radius-full)] px-6 py-3 text-foreground-primary transition-all duration-[var(--duration-fast)] !bg-[hsl(63,75%,51%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary-dark hover:shadow-medium hover:-translate-y-0.5 focus:!bg-primary-dark">+44 (0)20 7221 3292</Link>
          </Button>
          <Button asChild className="rounded-[var(--radius-full)] px-6 py-3 bg-primary text-foreground-primary hover:bg-primary hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)]">
            <Link to="https://portal.sloane.co.uk/signin" newTab>Client Portal</Link>
          </Button>
        </div>
      </div>
    </header>;
}